import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useUserStore } from "@store/user";
import {
  useGetActivationStats,
  ActivationStats,
} from "@api/hooks/doubleMateriality";

export enum DoubleMaterialitySurveyActivationStrategy {
  ALL = 0,
  ONLY_NEW_STAKEHOLDERS = 1,
  NO_ONE = 2,
}

interface DoubleMaterialityActivationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (strategy: DoubleMaterialitySurveyActivationStrategy) => void;
  surveyId: string;
}

export const DoubleMaterialityActivationModal: FC<DoubleMaterialityActivationModalProps> = ({
  open,
  onClose,
  onConfirm,
  surveyId,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [strategy, setStrategy] = useState<DoubleMaterialitySurveyActivationStrategy>(
    DoubleMaterialitySurveyActivationStrategy.ONLY_NEW_STAKEHOLDERS,
  );

  const {
    stats,
    isPending: loading,
    isError: error,
  } = useGetActivationStats(user, surveyId, open);

  useEffect(() => {
    if (!open) {
      setStrategy(DoubleMaterialitySurveyActivationStrategy.ONLY_NEW_STAKEHOLDERS);
    }
  }, [open]);

  const handleConfirm = (): void => {
    onConfirm(strategy);
    onClose();
  };

  const renderLabel = (
    label: string,
    count?: number,
    description?: string,
  ): JSX.Element => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body1">{label}</Typography>
        {count !== undefined && (
          <Chip
            label={count}
            size="small"
            sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
            color={count > 0 ? "primary" : "default"}
          />
        )}
      </Stack>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("doubleMateriality.activationModal.title", "Activate Survey Campaign")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "doubleMateriality.activationModal.description",
              "Choose who should receive an email invitation when activiting the campaign.",
            )}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : error ? (
            <Typography color="error" variant="body2">
              {t("doubleMateriality.activationModal.error", "Failed to load recipient counts.")}
            </Typography>
          ) : (
            <FormControl component="fieldset">
              <RadioGroup
                value={strategy}
                onChange={(e) =>
                  setStrategy(Number(e.target.value) as DoubleMaterialitySurveyActivationStrategy)
                }
              >
                <FormControlLabel
                  value={DoubleMaterialitySurveyActivationStrategy.ONLY_NEW_STAKEHOLDERS}
                  control={<Radio />}
                  sx={{ mb: 1, alignItems: 'flex-start', py: 0.5 }}
                  label={renderLabel(
                    t("doubleMateriality.activationModal.onlyNew", "Send only to new stakeholders"),
                    stats?.newStakeholdersCount,
                    t("doubleMateriality.activationModal.onlyNewDesc", "Stakeholders who haven't received an invitation yet.")
                  )}
                />
                <FormControlLabel
                  value={DoubleMaterialitySurveyActivationStrategy.ALL}
                  control={<Radio />}
                  sx={{ mb: 1, alignItems: 'flex-start', py: 0.5 }}
                  label={renderLabel(
                    t("doubleMateriality.activationModal.all", "Send to all assigned stakeholders"),
                    stats?.allStakeholdersCount,
                    t("doubleMateriality.activationModal.allDesc", "Resend to everyone who has assigned questions.")
                  )}
                />
                <FormControlLabel
                  value={DoubleMaterialitySurveyActivationStrategy.NO_ONE}
                  control={<Radio />}
                  sx={{ mb: 1, alignItems: 'flex-start', py: 0.5 }}
                  label={renderLabel(
                    t("doubleMateriality.activationModal.noOne", "Don't send emails"),
                    undefined,
                    t("doubleMateriality.activationModal.noOneDesc", "Activate without sending any notifications.")
                  )}
                />
              </RadioGroup>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {t("common.activate", "Activate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
